import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Gift, Mail, Copy, Check, Share2, Users, Euro, TrendingUp, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReferralsPage = () => {
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/referral/my-referrals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferralData(response.data);
    } catch (error) {
      console.error('Failed to fetch referral data:', error);
      toast.error('Fehler beim Laden der Empfehlungsdaten');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (!referralData) return;
    try {
      await navigator.clipboard.writeText(referralData.referral_url);
      setCopied(true);
      toast.success('Link kopiert!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Fehler beim Kopieren');
    }
  };

  const shareViaEmail = () => {
    if (!referralData) return;
    const subject = encodeURIComponent('Probiere CarCortex-AI aus!');
    const body = encodeURIComponent(
      `Hey!\n\nIch nutze CarCortex-AI für KI-gestützte Fahrzeugdiagnosen und bin begeistert. Schau es dir mal an:\n\n${referralData.referral_url}\n\nDu kannst damit bis zu 87% Reparaturkosten sparen!\n\nViele Grüße`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const shareViaWhatsApp = () => {
    if (!referralData) return;
    const text = encodeURIComponent(
      `Hey! Ich nutze CarCortex-AI für KI-gestützte Fahrzeugdiagnosen und bin begeistert. Schau es dir mal an: ${referralData.referral_url} 🚗🔧`
    );
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const shareViaTelegram = () => {
    if (!referralData) return;
    const text = encodeURIComponent(
      `Hey! Ich nutze CarCortex-AI für KI-gestützte Fahrzeugdiagnosen. Schau es dir an: ${referralData.referral_url}`
    );
    window.open(`https://t.me/share/url?url=${referralData.referral_url}&text=${text}`, '_blank');
  };

  const shareViaFacebook = () => {
    if (!referralData) return;
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralData.referral_url)}`, '_blank');
  };

  const shareViaTwitter = () => {
    if (!referralData) return;
    const text = encodeURIComponent('Ich nutze CarCortex-AI für KI-gestützte Fahrzeugdiagnosen. Probier es aus! 🚗');
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(referralData.referral_url)}`, '_blank');
  };

  if (loading) {
    return (
      <div className="p-8" style={{ backgroundColor: '#0B0F14', minHeight: '100vh' }}>
        <div className="max-w-6xl mx-auto">
          <p style={{ color: '#94A3B8' }}>Lädt...</p>
        </div>
      </div>
    );
  }

  if (!referralData) {
    return (
      <div className="p-8" style={{ backgroundColor: '#0B0F14', minHeight: '100vh' }}>
        <div className="max-w-6xl mx-auto">
          <p style={{ color: '#EF4444' }}>Fehler beim Laden der Daten</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8" style={{ backgroundColor: '#0B0F14', minHeight: '100vh' }}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Gift style={{ color: '#7CD4FD' }} size={32} />
            <h1 className="text-3xl font-bold" style={{ color: '#FFFFFF', fontFamily: 'Manrope' }}>
              Empfehlungsprogramm
            </h1>
          </div>
          <p style={{ color: '#E6EDF3', fontSize: '1.1rem' }}>
            Empfehle CarCortex-AI und erhalte CHF 10 Guthaben für jede erfolgreiche Empfehlung
          </p>
        </div>

        {/* Main Stats Grid */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <Euro style={{ color: '#22C55E' }} size={24} />
              </div>
              <div className="text-4xl font-bold mb-1" style={{ color: '#22C55E' }}>
                CHF {(referralData.available_credits || 0).toFixed(2)}
              </div>
              <div style={{ color: '#E6EDF3', fontSize: '0.875rem' }}>Verfügbares Guthaben</div>
            </CardContent>
          </Card>

          <Card style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <Users style={{ color: '#7CD4FD' }} size={24} />
              </div>
              <div className="text-4xl font-bold mb-1" style={{ color: '#7CD4FD' }}>
                {referralData.total_paid_referrals || 0}
              </div>
              <div style={{ color: '#E6EDF3', fontSize: '0.875rem' }}>Erfolgreiche Empfehlungen</div>
            </CardContent>
          </Card>

          <Card style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <Clock style={{ color: '#A78BFA' }} size={24} />
              </div>
              <div className="text-4xl font-bold mb-1" style={{ color: '#A78BFA' }}>
                CHF {(referralData.pending_credits || 0).toFixed(2)}
              </div>
              <div style={{ color: '#E6EDF3', fontSize: '0.875rem' }}>Ausstehend</div>
            </CardContent>
          </Card>

          <Card style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp style={{ color: '#F59E0B' }} size={24} />
              </div>
              <div className="text-4xl font-bold mb-1" style={{ color: '#F59E0B' }}>
                {referralData.total_clicks || 0}
              </div>
              <div style={{ color: '#E6EDF3', fontSize: '0.875rem' }}>Link-Klicks</div>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Referral Link Card */}
          <Card style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}>
            <CardHeader>
              <CardTitle style={{ color: '#FFFFFF' }}>Dein Empfehlungslink</CardTitle>
              <CardDescription style={{ color: '#D1D5DB' }}>
                Teile diesen Link mit Freunden und Familie
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={referralData.referral_url}
                  readOnly
                  className="flex-1 px-4 py-3 rounded-lg text-sm"
                  style={{
                    backgroundColor: '#374151',
                    border: '1px solid #4B5563',
                    color: '#FFFFFF'
                  }}
                />
                <Button
                  onClick={copyToClipboard}
                  style={{
                    backgroundColor: copied ? '#22C55E' : '#7CD4FD',
                    color: '#0B0F14',
                    fontWeight: '600'
                  }}
                >
                  {copied ? <Check size={20} /> : <Copy size={20} />}
                </Button>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-4 pt-4 border-t" style={{ borderColor: '#374151' }}>
                <div>
                  <div className="text-2xl font-bold" style={{ color: '#7CD4FD' }}>
                    {referralData.total_registrations || 0}
                  </div>
                  <div style={{ color: '#D1D5DB', fontSize: '0.875rem' }}>Registrierungen</div>
                </div>
                <div>
                  <div className="text-2xl font-bold" style={{ color: '#22C55E' }}>
                    CHF {((referralData.total_earned || referralData.total_credits_earned) || 0).toFixed(2)}
                  </div>
                  <div style={{ color: '#D1D5DB', fontSize: '0.875rem' }}>Gesamt verdient</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Share Buttons Card */}
          <Card style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}>
            <CardHeader>
              <CardTitle style={{ color: '#FFFFFF' }}>
                <Share2 className="inline mr-2" size={20} />
                Jetzt teilen
              </CardTitle>
              <CardDescription style={{ color: '#D1D5DB' }}>
                Wähle eine Plattform zum Teilen
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  onClick={shareViaEmail}
                  variant="outline"
                  className="flex items-center gap-2 justify-center hover:opacity-80 transition-opacity"
                  style={{
                    backgroundColor: '#374151',
                    borderColor: '#4B5563',
                    color: '#FFFFFF',
                    padding: '1rem'
                  }}
                >
                  <Mail size={20} />
                  E-Mail
                </Button>

                <Button
                  onClick={shareViaWhatsApp}
                  variant="outline"
                  className="flex items-center gap-2 justify-center hover:opacity-80 transition-opacity"
                  style={{
                    backgroundColor: '#374151',
                    borderColor: '#4B5563',
                    color: '#FFFFFF',
                    padding: '1rem'
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  WhatsApp
                </Button>

                <Button
                  onClick={shareViaTelegram}
                  variant="outline"
                  className="flex items-center gap-2 justify-center hover:opacity-80 transition-opacity"
                  style={{
                    backgroundColor: '#374151',
                    borderColor: '#4B5563',
                    color: '#FFFFFF',
                    padding: '1rem'
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121L8.08 13.768l-2.972-.924c-.645-.203-.658-.645.136-.954l11.617-4.48c.536-.194 1.006.128.832.954z"/>
                  </svg>
                  Telegram
                </Button>

                <Button
                  onClick={shareViaFacebook}
                  variant="outline"
                  className="flex items-center gap-2 justify-center hover:opacity-80 transition-opacity"
                  style={{
                    backgroundColor: '#374151',
                    borderColor: '#4B5563',
                    color: '#FFFFFF',
                    padding: '1rem'
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  Facebook
                </Button>

                <Button
                  onClick={shareViaTwitter}
                  variant="outline"
                  className="flex items-center gap-2 justify-center hover:opacity-80 transition-opacity"
                  style={{
                    backgroundColor: '#374151',
                    borderColor: '#4B5563',
                    color: '#FFFFFF',
                    padding: '1rem'
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                  </svg>
                  Twitter
                </Button>

                <Button
                  onClick={copyToClipboard}
                  variant="outline"
                  className="flex items-center gap-2 justify-center hover:opacity-80 transition-opacity col-span-2"
                  style={{
                    backgroundColor: copied ? '#22C55E' : '#374151',
                    borderColor: copied ? '#16A34A' : '#4B5563',
                    color: '#FFFFFF',
                    padding: '1rem'
                  }}
                >
                  {copied ? (
                    <>
                      <Check size={20} />
                      Link kopiert!
                    </>
                  ) : (
                    <>
                      <Copy size={20} />
                      Link kopieren
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* How it works */}
        <Card className="mt-8" style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}>
          <CardHeader>
            <CardTitle style={{ color: '#FFFFFF' }}>So funktioniert's</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <div className="text-3xl font-bold mb-2" style={{ color: '#7CD4FD' }}>1</div>
                <h3 className="text-lg font-semibold mb-2" style={{ color: '#FFFFFF' }}>Teilen</h3>
                <p style={{ color: '#D1D5DB', lineHeight: '1.6' }}>
                  Teile deinen einzigartigen Empfehlungslink mit Freunden, Familie oder auf Social Media
                </p>
              </div>
              <div>
                <div className="text-3xl font-bold mb-2" style={{ color: '#7CD4FD' }}>2</div>
                <h3 className="text-lg font-semibold mb-2" style={{ color: '#FFFFFF' }}>Registrierung</h3>
                <p style={{ color: '#D1D5DB', lineHeight: '1.6' }}>
                  Deine Freunde registrieren sich über deinen Link und schließen ein Abonnement ab
                </p>
              </div>
              <div>
                <div className="text-3xl font-bold mb-2" style={{ color: '#22C55E' }}>3</div>
                <h3 className="text-lg font-semibold mb-2" style={{ color: '#FFFFFF' }}>Guthaben erhalten</h3>
                <p style={{ color: '#D1D5DB', lineHeight: '1.6' }}>
                  Du erhältst automatisch CHF 10 Guthaben, sobald die Zahlung eingegangen ist
                </p>
              </div>
            </div>

            <div className="mt-6 p-4 rounded-lg" style={{ backgroundColor: '#374151' }}>
              <p style={{ color: '#FFFFFF', fontSize: '0.95rem', lineHeight: '1.6' }}>
                💡 <strong>Tipp:</strong> Credits verfallen nie und können für Abonnementverlängerungen oder Upgrades verwendet werden. Es gibt keine Obergrenze - empfehle so viele Freunde wie du möchtest!
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ReferralsPage;
